// The flat plan editor: correct a generated flat by hand and save it as an
// example of what good enough looks like.
//
// It edits a copy of the flat's rooms in plan coordinates, with the plan's
// own geometry (Clipper booleans), and redraws through the same renderer as
// the plan view. Doors, windows and furniture are recomputed after every
// change by the same rules the engine uses (rooms.ts), so what is on screen
// is what the engine would make of the edited rooms. Saved examples go to
// .data/examples/ through the dev server (scripts/dev.mjs), or download as
// JSON where there is no server to take them.
import { area, difference, distanceTo, grow, intersection, join, nearestOnOutline, type MultiPoly, type Pt } from "../domain/geom.ts";
import type { Room, RoomKind, Unit } from "../domain/layout.ts";
import { flatOpenings, reachableRooms } from "../domain/openings.ts";
import { furnish } from "../domain/furniture.ts";
import { flatPlanSvg, flatSummary, planFrame, roomChecks, roomNames, ZONE, ZONE_FILL } from "../domain/export/flatplan.ts";

const KINDS: [RoomKind, string][] = [["living", "Cameră de zi"], ["bedroom", "Dormitor"], ["kitchen", "Bucătărie"], ["bath", "Baie"], ["hall", "Hol"], ["storage", "Debara / dressing"]];

type Mode = "select" | "cutV" | "cutH" | "merge" | "door";
const MODE_HELP: Record<Mode, string> = {
  select: "Click pe o cameră ca s-o alegi. Trage de un perete al camerei alese ca să-l muți.",
  cutV: "Click în cameră: o tai vertical prin acel punct.",
  cutH: "Click în cameră: o tai orizontal prin acel punct.",
  merge: "Click pe camera vecină cu care s-o unești.",
  door: "Click pe conturul apartamentului, unde vrei ușa de intrare.",
};

export interface EditorContext {
  name: string;
  option: string;
  storeyIndex: number;
  onClose: () => void;
}

const copyRooms = (rooms: Room[]): Room[] => JSON.parse(JSON.stringify(rooms));

export function editFlat(host: HTMLElement, flat: Unit, storey: MultiPoly, ctx: EditorContext) {
  const original = { rooms: copyRooms(flat.rooms ?? []), door: flat.door ? ([...flat.door] as Pt) : undefined };
  let rooms = copyRooms(original.rooms);
  let door = original.door;
  let selected: number | undefined;
  let mode: Mode = "select";
  const history: { rooms: Room[]; door?: Pt }[] = [];
  let message = "";
  // the frame is the original outline's, so the drawing does not move as rooms change
  const frame = planFrame(flat);

  const push = () => { history.push({ rooms: copyRooms(rooms), door: door ? ([...door] as Pt) : undefined }); if (history.length > 60) history.shift(); };
  const unit = (): Unit => {
    const beds = rooms.filter((r) => r.kind === "bedroom").length;
    const u: Unit = { ...flat, type: String(Math.max(1, Math.min(5, beds + 1))) as Unit["type"], rooms, door };
    u.openings = flatOpenings(u, storey);
    return u;
  };
  const roomAt = (p: Pt) => rooms.findIndex((r) => distanceTo(p, r.polygon) === 0);
  const largestPiece = (mp: MultiPoly): MultiPoly => (mp.length > 1 ? [mp.reduce((b, x) => (area([x]) > area([b]) ? x : b))] : mp);
  const tidy = () => { rooms = rooms.map((r) => ({ ...r, polygon: largestPiece(r.polygon) })).filter((r) => area(r.polygon) > 0.3); if (selected != null && selected >= rooms.length) selected = undefined; };
  const wallTo = (a: MultiPoly, b: MultiPoly) => area(intersection(grow(a, 0.05), b)) / 0.05;

  /** Cut room k by the line through p along d. */
  const cut = (k: number, p: Pt, d: Pt) => {
    const n: Pt = [-d[1], d[0]], B = 100;
    const half: MultiPoly = [[[[p[0] - d[0] * B, p[1] - d[1] * B], [p[0] + d[0] * B, p[1] + d[1] * B], [p[0] + d[0] * B + n[0] * B, p[1] + d[1] * B + n[1] * B], [p[0] - d[0] * B + n[0] * B, p[1] - d[1] * B + n[1] * B]]]];
    const a = intersection(rooms[k].polygon, half), b = difference(rooms[k].polygon, half);
    if (area(a) < 0.5 || area(b) < 0.5) { message = "Tăietura ar lăsa o bucată prea mică."; return; }
    push();
    const [big, small] = area(a) >= area(b) ? [a, b] : [b, a];
    rooms[k] = { ...rooms[k], polygon: largestPiece(big) };
    // a piece cut from the living room is its kitchen if the flat has none, else a bedroom; otherwise the same kind
    const kind: RoomKind = rooms[k].kind !== "living" ? rooms[k].kind : rooms.some((r) => r.kind === "kitchen") ? "bedroom" : "kitchen";
    rooms.push({ kind, polygon: largestPiece(small) });
    selected = rooms.length - 1;
    message = "Camera e tăiată în două; alege tipul noii camere.";
  };

  /** Move one edge of room k by delta along its outward normal; the room beside it gives or takes the strip. */
  const moveWall = (k: number, a: Pt, b: Pt, nOut: Pt, delta: number) => {
    if (Math.abs(delta) < 0.04) return;
    const strip: MultiPoly = [[[a, b, [b[0] + nOut[0] * delta, b[1] + nOut[1] * delta], [a[0] + nOut[0] * delta, a[1] + nOut[1] * delta]]]];
    push();
    if (delta > 0) {
      const gain = intersection(strip, flat.polygon);
      if (area(gain) < 0.05) { history.pop(); message = "Peretele e pe conturul apartamentului."; return; }
      rooms = rooms.map((r, i) => (i === k ? { ...r, polygon: join(r.polygon, gain) } : { ...r, polygon: difference(r.polygon, grow(gain, 0.002)) }));
    } else {
      const piece = intersection(strip, rooms[k].polygon);
      const host = rooms.map((r, i) => [i, i === k ? -1 : wallTo(piece, r.polygon)] as const).sort((x, y) => y[1] - x[1])[0];
      if (!host || host[1] < 0.2) { history.pop(); message = "Nu e nicio cameră dincolo de perete care să preia suprafața."; return; }
      rooms = rooms.map((r, i) => (i === k ? { ...r, polygon: difference(r.polygon, grow(piece, 0.002)) } : i === host[0] ? { ...r, polygon: join(r.polygon, piece) } : r));
    }
    tidy();
    message = "";
  };

  const paint = () => {
    const u = unit();
    const svg = flatPlanSvg(u, storey, furnish(u), { interactive: true, selected });
    const names = roomNames(u);
    const checks = roomChecks(u);
    const unreachable = rooms.length - reachableRooms(u).size;
    const sel = selected != null ? rooms[selected] : undefined;
    host.innerHTML = `<div class="dhead"><b>Editor · apartament ${flat.id} · ${u.type === "1" ? "garsonieră" : `${u.type} camere`}</b>
        <span class="row" style="gap:6px"><button class="btn" id="eundo" ${history.length ? "" : "disabled"}>Anulează</button><button class="btn" id="ereset">Revino la generat</button><button class="btn" id="eclose">Înapoi la plan</button></span></div>
      <div class="fp">
        <div class="fpsvg" id="ecanvas" style="touch-action:none">${svg}</div>
        <div class="fpside">
          <div class="seg emodes">${(["select", "cutV", "cutH", "merge", "door"] as Mode[]).map((m) => `<button data-mode="${m}" class="${m === mode ? "on" : ""}">${{ select: "Alege / mută perete", cutV: "Taie ↕", cutH: "Taie ↔", merge: "Unește", door: "Ușa de intrare" }[m]}</button>`).join("")}</div>
          <div class="sub" style="margin:6px 0 10px">${MODE_HELP[mode]}</div>
          ${sel ? `<h2>${names[selected!]} · ${(area(sel.polygon) * 0.92).toFixed(1).replace(".", ",")} m²</h2>
            <div class="field"><label>Tipul camerei</label><select id="ekind">${KINDS.map(([k, n]) => `<option value="${k}" ${k === sel.kind ? "selected" : ""}>${n}</option>`).join("")}</select></div>
            <button class="btn" id="edelete" style="width:100%">Șterge camera (o lipesc de vecina cu peretele cel mai lung)</button>` : `<div class="sub">Nicio cameră aleasă.</div>`}
          ${message ? `<div class="sub" style="color:var(--warn);margin-top:8px">${message}</div>` : ""}
          <h2>Reguli</h2>
          <table class="t">${checks.map((c) => `<tr><td class="${c.ok ? "pass" : "fail"}" style="width:18px">${c.ok ? "✓" : "!"}</td><td>${c.text}</td></tr>`).join("")}
            ${unreachable ? `<tr><td class="fail">!</td><td>${unreachable} ${unreachable === 1 ? "cameră fără ușă" : "camere fără ușă"}: ușile se pun singure după reguli; o cameră la care nu ajunge nicio ușă permisă rămâne fără</td></tr>` : ""}</table>
          <h2>Camere</h2>
          <table class="t">${rooms.map((r, i) => `<tr data-pick="${i}" style="cursor:pointer${i === selected ? ";font-weight:600" : ""}"><td><span class="swatch" style="background:${ZONE_FILL[ZONE[r.kind]]}"></span>${names[i]}</td><td class="n">${(area(r.polygon) * 0.92).toFixed(1).replace(".", ",")} m²</td></tr>`).join("")}</table>
          <table class="t" style="margin-top:6px">${flatSummary(u).slice(0, 2).map((x) => `<tr><td>${x.label}</td><td class="n">${x.value}</td></tr>`).join("")}</table>
          <h2>Salvează pentru învățare</h2>
          <div class="seg" id="everdict"><button data-v="good" class="on">E bun așa</button><button data-v="better">Corectat de mine</button><button data-v="bad">Generatul e greșit</button></div>
          <textarea id="enote" rows="3" style="width:100%;margin-top:6px" placeholder="Ce ai schimbat și de ce (de ex. „bucătăria lângă living, nu lângă dormitor”)"></textarea>
          <button class="btn primary" id="esave" style="width:100%;margin-top:6px">Salvează ca exemplu</button>
          <div class="sub" id="esaved" style="margin-top:6px"></div>
        </div>
      </div>`;
    wire(u);
  };

  let verdict = "better";
  const wire = (u: Unit) => {
    const $ = (s: string) => host.querySelector(s) as HTMLElement | null;
    $("#eclose")!.onclick = ctx.onClose;
    $("#eundo")!.onclick = () => { const h = history.pop(); if (h) { rooms = h.rooms; door = h.door; selected = undefined; paint(); } };
    $("#ereset")!.onclick = () => { push(); rooms = copyRooms(original.rooms); door = original.door; selected = undefined; paint(); };
    host.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((b) => (b.onclick = () => { mode = b.dataset.mode as Mode; message = mode !== "select" && mode !== "door" && selected == null ? "Alege întâi o cameră." : ""; paint(); }));
    host.querySelectorAll<HTMLElement>("[data-pick]").forEach((tr) => (tr.onclick = () => { selected = +tr.dataset.pick!; paint(); }));
    const kind = $("#ekind") as HTMLSelectElement | null;
    if (kind) kind.onchange = () => { push(); rooms[selected!] = { ...rooms[selected!], kind: kind.value as RoomKind }; paint(); };
    const del = $("#edelete");
    if (del) del.onclick = () => {
      const k = selected!;
      const host2 = rooms.map((r, i) => [i, i === k ? -1 : wallTo(rooms[k].polygon, r.polygon)] as const).sort((a, b) => b[1] - a[1])[0];
      if (!host2 || host2[1] < 0.2) { message = "Camera nu are vecină cu care să se unească."; paint(); return; }
      push();
      rooms[host2[0]] = { ...rooms[host2[0]], polygon: join(rooms[host2[0]].polygon, rooms[k].polygon) };
      rooms.splice(k, 1);
      selected = host2[0] > k ? host2[0] - 1 : host2[0];
      tidy();
      paint();
    };
    host.querySelectorAll<HTMLButtonElement>("#everdict button").forEach((b) => {
      b.classList.toggle("on", b.dataset.v === verdict);
      b.onclick = () => { verdict = b.dataset.v!; host.querySelectorAll("#everdict button").forEach((x) => x.classList.toggle("on", x === b)); };
    });
    $("#esave")!.onclick = () => save(u, ($("#enote") as HTMLTextAreaElement).value, $("#esaved")!);

    // the drawing: points from the screen to the plan
    const svgEl = host.querySelector("#ecanvas svg") as SVGSVGElement | null;
    if (!svgEl) return;
    const toPage = (e: PointerEvent): Pt => { const pt = svgEl.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY; const q = pt.matrixTransform(svgEl.getScreenCTM()!.inverse()); return [q.x, q.y]; };
    const toPlan = (e: PointerEvent): Pt => frame.inv(toPage(e));
    let drag: { k: number; a: Pt; b: Pt; n: Pt; start: Pt; line: SVGLineElement } | undefined;

    svgEl.addEventListener("pointerdown", (e) => {
      const p = toPlan(e);
      if (mode === "door") {
        push();
        door = nearestOnOutline(p, flat.polygon);
        mode = "select";
        paint();
        return;
      }
      const k = roomAt(p);
      if (mode === "cutV" || mode === "cutH") {
        const target = k >= 0 ? k : selected;
        if (target == null || target < 0) return;
        cut(target, p, mode === "cutV" ? frame.uy : frame.ux);
        mode = "select";
        paint();
        return;
      }
      if (mode === "merge") {
        if (selected == null || k < 0 || k === selected) return;
        const j = join(rooms[selected].polygon, rooms[k].polygon);
        if (j.length !== 1) { message = "Camerele nu se ating: nu le pot uni."; paint(); return; }
        push();
        rooms[selected] = { ...rooms[selected], polygon: j };
        rooms.splice(k, 1);
        if (k < selected) selected--;
        mode = "select";
        paint();
        return;
      }
      // select mode: grab a wall of the selected room if the press is on one, else select
      if (selected != null) {
        const page = toPage(e);
        let best: { a: Pt; b: Pt; d: number } | undefined;
        for (const poly of rooms[selected].polygon) for (const ring of poly) ring.forEach((a, i) => {
          const b = ring[(i + 1) % ring.length];
          const [A, B] = [frame.P(a), frame.P(b)];
          const L = Math.hypot(B[0] - A[0], B[1] - A[1]);
          if (L < 0.3) return;
          const t = Math.max(0, Math.min(1, ((page[0] - A[0]) * (B[0] - A[0]) + (page[1] - A[1]) * (B[1] - A[1])) / (L * L)));
          const d = Math.hypot(A[0] + t * (B[0] - A[0]) - page[0], A[1] + t * (B[1] - A[1]) - page[1]);
          if (d < 0.25 && (!best || d < best.d)) best = { a, b, d };
        });
        if (best) {
          const { a, b } = best;
          const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
          let n: Pt = [-(b[1] - a[1]) / L, (b[0] - a[0]) / L];
          const mid: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
          if (distanceTo([mid[0] + n[0] * 0.05, mid[1] + n[1] * 0.05], rooms[selected].polygon) === 0) n = [-n[0], -n[1]];
          const [A, B] = [frame.P(a), frame.P(b)];
          const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
          line.setAttribute("x1", String(A[0])); line.setAttribute("y1", String(A[1])); line.setAttribute("x2", String(B[0])); line.setAttribute("y2", String(B[1]));
          line.setAttribute("stroke", "#2f5bea"); line.setAttribute("stroke-width", "0.12"); line.setAttribute("stroke-dasharray", "0.2 0.1");
          svgEl.appendChild(line);
          drag = { k: selected, a, b, n, start: p, line };
          svgEl.setPointerCapture(e.pointerId);
          return;
        }
      }
      selected = k >= 0 ? k : undefined;
      paint();
    });
    svgEl.addEventListener("pointermove", (e) => {
      if (!drag) return;
      const p = toPlan(e);
      const delta = Math.round(((p[0] - drag.start[0]) * drag.n[0] + (p[1] - drag.start[1]) * drag.n[1]) * 20) / 20;
      const [A, B] = [frame.P([drag.a[0] + drag.n[0] * delta, drag.a[1] + drag.n[1] * delta]), frame.P([drag.b[0] + drag.n[0] * delta, drag.b[1] + drag.n[1] * delta])];
      drag.line.setAttribute("x1", String(A[0])); drag.line.setAttribute("y1", String(A[1])); drag.line.setAttribute("x2", String(B[0])); drag.line.setAttribute("y2", String(B[1]));
    });
    svgEl.addEventListener("pointerup", (e) => {
      if (!drag) return;
      const p = toPlan(e);
      const delta = Math.round(((p[0] - drag.start[0]) * drag.n[0] + (p[1] - drag.start[1]) * drag.n[1]) * 20) / 20;
      const d = drag;
      drag = undefined;
      moveWall(d.k, d.a, d.b, d.n, delta);
      paint();
    });
  };

  const save = async (u: Unit, note: string, out: HTMLElement) => {
    const data = {
      savedAt: new Date().toISOString(),
      project: ctx.name, option: ctx.option, storey: ctx.storeyIndex, flat: flat.id, origin: flat.origin,
      verdict, note,
      generated: { type: flat.type, door: original.door, rooms: original.rooms, polygon: flat.polygon },
      edited: { type: u.type, door, rooms: u.rooms, openings: u.openings },
      checks: roomChecks(u),
    };
    try {
      const r = await fetch("/api/examples", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(data) });
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      out.textContent = `Salvat în ${j.saved}. Îl citesc de acolo.`;
    } catch {
      // no dev server (the published page): hand the file over instead
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      a.download = `exemplu-${flat.id}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      out.textContent = "Descărcat ca fișier JSON: trimite-mi-l.";
    }
  };

  paint();
}
